import { makeAutoObservable } from 'mobx';
import IFavorite, { FavoriteTargetType, IFavoritePropertyDetails } from "../interfaces/IFavorite";
import IProperty from "../interfaces/IProperty";
import FavoriteApiService from "../services/favoriteApiService";

export default class FavoriteStore {
    isLoading: boolean = false;
    isError: boolean = false;
    favorites: IFavorite[] = [];
    favoritePropertiesDetails: IFavoritePropertyDetails[] = [];

    constructor(
        private favoriteApiService: FavoriteApiService,
    ) {
        makeAutoObservable(this);
    }

    setIsLoading(value: boolean) {
        this.isLoading = value;
    }

    setIsError(value: boolean) {
        this.isError = value;
    }

    setFavorites(favorites: IFavorite[]) {
        this.favorites = favorites;
    }

    setFavoritePropertiesDetails(favoritePropertiesDetails: IFavoritePropertyDetails[]) {
        this.favoritePropertiesDetails = favoritePropertiesDetails;
    }

    initialize() {
        this.setIsLoading(true);

        Promise.all([
            this.favoriteApiService.getFavorites(),
            this.favoriteApiService.getFavoritePropertiesDetails()
        ]).then(([favorites, favoritePropertiesDetails]) => {
            this.setFavorites(favorites);
            this.setFavoritePropertiesDetails(favoritePropertiesDetails);
        }).catch(() => {
            this.setIsError(true)
        }).finally(() => {
            this.setIsLoading(false);
        });
    }

    async addFavoriteProperty(property: IProperty) {
        const favoritePropertyDetails = await this.favoriteApiService.createFavoritePropertyDetails(property);
        this.setFavoritePropertiesDetails([
            ...this.favoritePropertiesDetails,
            favoritePropertyDetails,
        ]);

        await this.addFavoriteInternal(favoritePropertyDetails.id, FavoriteTargetType.Property);
    }

    async addFavoriteLead(id: number) {
        await this.addFavoriteInternal(id, FavoriteTargetType.Lead);
    }

    async addFavoriteLeaseComp(id: number) {
        await this.addFavoriteInternal(id, FavoriteTargetType.LeaseComp);
    }

    async addFavoriteSaleComp(id: number) {
        await this.addFavoriteInternal(id, FavoriteTargetType.SaleComp);
    }

    async addFavoriteDeal(id: number) {
        await this.addFavoriteInternal(id, FavoriteTargetType.Deal);
    }

    async addFavoriteTenantInMarket(id: number) {
        await this.addFavoriteInternal(id, FavoriteTargetType.TenantInMarket);
    }

    async addFavoriteCompany(id: number) {
        await this.addFavoriteInternal(id, FavoriteTargetType.Company);
    }

    async deleteFavoriteProperty(favoritePropertyDetailsId: number) {
        await this.favoriteApiService.deleteFavoritePropertyDetails(favoritePropertyDetailsId);
        this.setFavoritePropertiesDetails(this.favoritePropertiesDetails.filter(fpd => fpd.id !== favoritePropertyDetailsId));

        await this.deleteFavoriteInternal(favoritePropertyDetailsId, FavoriteTargetType.Property);
    }

    async deleteFavoriteLead(id: number) {
        await this.deleteFavoriteInternal(id, FavoriteTargetType.Lead);
    }

    async deleteFavoriteLeaseComp(id: number) {
        await this.deleteFavoriteInternal(id, FavoriteTargetType.LeaseComp);
    }

    async deleteFavoriteSaleComp(id: number) {
        await this.deleteFavoriteInternal(id, FavoriteTargetType.SaleComp);
    }

    async deleteFavoriteDeal(id: number) {
        await this.deleteFavoriteInternal(id, FavoriteTargetType.Deal);
    }

    async deleteFavoriteTenantInMarket(id: number) {
        await this.deleteFavoriteInternal(id, FavoriteTargetType.TenantInMarket);
    }

    async deleteFavoriteCompany(id: number) {
        await this.deleteFavoriteInternal(id, FavoriteTargetType.Company);
    }

    removeFavoriteLead(id: number) {
        this.setFavorites(this.favorites.filter(f => f.targetId !== id || f.targetType !== FavoriteTargetType.Lead));
    }

    clear() {
        this.setIsLoading(false);
        this.setIsError(false);
        this.setFavorites([]);
        this.setFavoritePropertiesDetails([]);
    }

    private async addFavoriteInternal(targetId: number, targetType: FavoriteTargetType) {
        this.setFavorites([
            ...this.favorites,
            await this.favoriteApiService.createFavorite(targetId, targetType),
        ]);
    }

    private async deleteFavoriteInternal(targetId: number, targetType: FavoriteTargetType) {
        const favoriteId = this.favorites.find(f => f.targetId === targetId && f.targetType === targetType)!.id;
        await this.favoriteApiService.deleteFavorite(favoriteId);
        this.setFavorites(this.favorites.filter(f => f.id !== favoriteId));
    }
}